import React, {useEffect, useMemo} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled, {css} from 'styled-components';

import Icon from 'components/icons/icon';

import theme, {bp, color} from 'settings/theme';

type Props = {
  totalCount: number,
  itemsPerPage: number,
  currentPage: number,
  pagesToShow: number,
  urlMode?: string,
  pathTemplate?: string,
  pathTemplateFirstPage?: string,
  scrollToTop?: boolean,
  redirect?: boolean,
  showDirectionButtons?: boolean,
  showLimitButtons?: boolean,
  whenPageChanged?: Function,
  className?: string,
  size?: string
}

// Component
const Pagination = (props: Props) => {

  const router = useRouter()


  // Page Count
  const pageCount = useMemo(() => Math.ceil((props.totalCount || 0) / props.itemsPerPage), [props.totalCount, props.itemsPerPage])

  // Previous Page Number
  const previousPageNumber = useMemo(() => Math.max(props.currentPage - 1, 1), [props.currentPage])

  // Next Page Number
  const nextPageNumber = useMemo(() => Math.min(props.currentPage + 1, pageCount), [props.currentPage, pageCount])


  // Numbers
  const numbers = useMemo(() => {

    let numbers: { pageNumber: number; isCurrentPage: boolean; }[] = [];

    const numbersEitherSide = Math.ceil((props.pagesToShow-1)/2);

    let firstPageNumber = Math.max(props.currentPage - numbersEitherSide, 1);
    firstPageNumber = Math.min(firstPageNumber, (Math.max(pageCount - (props.pagesToShow - 1), 1)));

    let lastPageNumber = Math.min(firstPageNumber + props.pagesToShow - 1, pageCount);

    for (let i = firstPageNumber; i <= lastPageNumber; i++) { 
      numbers.push({
        pageNumber: i,
        isCurrentPage: i === props.currentPage
      });
    }

    return numbers;

  }, [props.pagesToShow, props.totalCount, props.itemsPerPage, props.currentPage])

    

  // Get URL with page number
  const createLinkAs = (pageNumber) => {

    let path = router.asPath.includes('?') ? router.asPath.substring(0, router.asPath.indexOf('?')) : router.asPath;
    let query = {...router.query};
      
    // If we're in querystring mode we need to set the page number in the query string
    if(props.urlMode === 'query'){

      if(pageNumber  > 1){
        query.page = pageNumber;
      }
      else{
        delete query.page;
      }

    }


    // Remove anything from the query which is a url param
    let queryString = Object.keys(query)
    .filter((key) => {
        return (router.pathname.includes(`[${key}]`) || router.pathname.includes(`[...${key}]`) || router.pathname.includes(`[[...${key}]]`)) === false
    })
    // Convert key/value pairs to querystring parts
    .map((key) => {
        return `${key}=${query[key]}`
    }).join('&');



    // If we're in path mode we need to set the page number in the path
    if(props.urlMode === 'path'){

      if (!props.pathTemplate){
        console.warn("Pagination control is in path mode but has no path template specified");
      }

      if(pageNumber  === 1 && props.pathTemplateFirstPage){
        path = props.pathTemplateFirstPage.replace('{page}', pageNumber);
      }
      else{
        path = (props.pathTemplate ?? '').replace('{page}', pageNumber);
      } 

    }

    // Create the final link
    let linkAs = path;

    if(queryString.length > 0){
      linkAs += `?${queryString}`;
    }

    return linkAs;

  }


  // Create Href
  const createLinkHref = (pageNumber) => {

    let pathname = router.pathname;

    // Query
    let query = {...router.query};

    if(props.urlMode === 'query'){

      if(pageNumber > 1){
        query.page = pageNumber;
      }
      else{
        delete query.page;
      }
        
    }

    return {
      pathname,
      query
    };
      
  }


  // Handle Button Click
  const handleButtonClick = (e, pageNumber) => {

    // Unless it's switched off, scroll to the top of the page
    if(props.scrollToTop !== false){
      window.scrollTo(0, 0);
    }

    // If redirecting is switched off, stop the url from changing
    if(props.redirect === false){
      e.preventDefault();
    }

    // Run whenChanged function
    if(props.whenPageChanged){
      props.whenPageChanged(pageNumber);
    }

  }


  return (

    <StyledPagination  
      {...props}
      disableFirst={props.currentPage === 1}
      disablePrevious={props.currentPage === 1}
      disableNext={props.currentPage === pageCount}
      disableLast={props.currentPage === pageCount}
      className={`pagination ${props.className ? props.className : ''}`}
    >
        
      {/* First Button */}
      {props.showLimitButtons !== false &&
        <div className="pagination_first">
          <Link
            href={createLinkHref(1)}
            as={createLinkAs(1)}
            scroll={false}
            onClick={(e) => handleButtonClick(e, 1)}
            className="pagination-button"
          >
            <Icon iconName="pagination-arrow-line" iconDirection="left" />
          </Link>
        </div>
      }
      

      {/* Previous Button */}
      <div className="pagination_previous">
        <Link
          href={createLinkHref(previousPageNumber)}
          as={createLinkAs(previousPageNumber)}
          scroll={false}
          onClick={(e) => handleButtonClick(e, previousPageNumber)}
          className="pagination-button"
        >
          <Icon iconName="pagination-arrow" iconDirection="left" />
        </Link>
      </div>
      

      {/* Number Buttons */}
      <div className="pagination_numbers">

        {numbers.map((number) => {
          return (
            <React.Fragment key={number.pageNumber}>
              <Link
                href={createLinkHref(number.pageNumber)}
                as={createLinkAs(number.pageNumber)}
                scroll={false}
                className={`pagination-button ${number.isCurrentPage ? 'is-current' : ''}`}
                onClick={(e) => handleButtonClick(e, number.pageNumber)}
              >
                {number.pageNumber}
              </Link>
            </React.Fragment>
          );
        })}

      </div>

      {/* Next Page */}
      <div className="pagination_next">
        <Link
          href={createLinkHref(nextPageNumber)}
          as={createLinkAs(nextPageNumber)}
          scroll={false}
          className="pagination-button"
          onClick={(e) => handleButtonClick(e, nextPageNumber)}
        >
          <Icon iconName="pagination-arrow" iconDirection="right" />
        </Link>
      </div>

      {/* Last Page */}
      {props.showLimitButtons !== false &&
        <div className="pagination_last">
          <Link
            href={createLinkHref(pageCount)}
            as={createLinkAs(pageCount)}
            scroll={false}
            className="pagination-button"
            onClick={(e) => handleButtonClick(e, pageCount)}
          >
            <Icon iconName="pagination-arrow-line" iconDirection="right" />
          </Link>
        </div>
      }
        
    </StyledPagination>
    
  );

}

Pagination.defaultProps = {
  urlMode: 'query',
  itemsPerPage: 12,
  pagesToShow: 5,
  showDirectionButtons: true,
  showLimitButtons: true
};

export default Pagination;


const StyledPagination = styled.div<Props & {
  disableFirst: Boolean,
  disablePrevious: Boolean,
  disableNext: Boolean,
  disableLast: Boolean
}>`
        
  display: inline-flex;
  color: white;

  /* Spacing between items */
  
  margin-right: -4px;
  
  > *{
    margin-right: 4px;
  }



  /* Buttons */
  a{
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    font-size: 14px;
    border-radius: 3px;
    background-color: ${color.grey(70)};
    transition: all 0.15s ease-out;

    .icon{

      width: 10px;
      height: 10px;

      svg{
        fill: white;
      }

    }

    &:hover{
      background-color: ${color.grey(50)};
    }

    &.is-current{
      background-color: ${color.primary};
    }

  }
    

    /* First */
    ${props => (props.disableFirst === true) && 
      css`
        .pagination_first{
          opacity: 0.3;
          a{cursor: default;}
        }
      `
    }


    /* Previous */
    ${props => (props.disablePrevious === true) && 
      css`
        .pagination_previous{
          opacity: 0.3;
          a{cursor: default;}
        }
      `
    }


    /* Numbers */
    .pagination_numbers{

      display: flex;

      margin-right: 0px;

      > *{
        margin-right: 4px;
      }

    }


    /* Next */
    ${props => (props.disableNext === true) && 
      css`
        .pagination_next{
          opacity: 0.3;
          a{cursor: default;}
        }
      `
    }


    /* Last */
    ${props => (props.disableLast === true) && 
      css`
        .pagination_last{
          opacity: 0.3;
          a{cursor: default;}
        }
      `
    }



    ${props => (props.size !== 'small') && 

      css`

        @media (min-width: ${bp(3)}) {

          margin-right: -8px;

          > *{
            margin-right: 8px;
          }

          a{

            width: 32px;
            height: 32px;
            padding-top: 1px;
            font-size: 16px;
            border-radius: 4px;

            .icon{

              width: 14px;
              height: 14px;

            }

          }

          .pagination_numbers{

            margin-right: 0px;

            > *{
              margin-right: 8px;
            }

          }

        }


      `

    }


`